import { match } from 'ts-pattern';
import { z } from 'zod';
import type {
  PermissionGroupListItem,
  QueryPermissionGroupListParams,
} from '../services/use-query-permission-group-list';

export const permissionGroupStatusSchema = z.enum(['enabled', 'disabled']);

const normalizeName = (value: string) => value.trim().toLowerCase();

export type PermissionGroupInfoSchemaOptions = {
  reservedNames: string[];
  requiredMessage: string;
  duplicatedMessage: string;
};

export const createPermissionGroupInfoSchema = ({
  reservedNames,
  requiredMessage,
  duplicatedMessage,
}: PermissionGroupInfoSchemaOptions) =>
  z.object({
    name: z
      .string()
      .max(50)
      .refine((value) => value.trim().length > 0, requiredMessage)
      .refine(
        (value) =>
          !reservedNames.some(
            (reserved) => normalizeName(reserved) === normalizeName(value),
          ),
        duplicatedMessage,
      ),
    description: z.string().max(200),
  });

export type PermissionGroup = Pick<
  PermissionGroupListItem,
  'funcGroupId' | 'groupName' | 'description' | 'lockVersion'
>;

export const permissionGroupSearchSchema = z.object({
  keyword: z.string().optional(),
  status: z.union([permissionGroupStatusSchema, z.literal('')]).optional(),
  groupId: z.string().optional(),
});

export type PermissionGroupSearchParams = z.infer<
  typeof permissionGroupSearchSchema
>;

export const toPermissionGroupListParams = (
  search: PermissionGroupSearchParams,
): QueryPermissionGroupListParams => ({
  keyword: search.keyword || undefined,
  activeFlag: match(search.status)
    .with('enabled', () => true)
    .with('disabled', () => false)
    .otherwise(() => undefined),
});
